/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import type { IProps } from './sidebar-container';
import { NavItem as NavItemType } from './types';

import Logo from './components/logo';
import NavItem from './components/nav-item';
import ChatButton from './components/chat-button';

import history from '../../../../shared/history';
import useBaseRoute from '../../../../shared/hooks/use-base-route';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import { Routes } from '../../../../shared/routes';
import { getHasVisitedAgencyPortal } from '../../../agency-portal/helper';

import {
  UNREAD_MAX_LIMIT,
  getIsSidebarInfoShown,
  isShowSidebarIntro,
  setIsSidebarInfoShown,
} from './utils/helper';
import { filterNavItems } from './utils/filter-nav-items';
import {
  executeOnRequestStatus,
  getIsRequestSucceeded,
} from '../../../../shared/utils';
import IntroPopover from './components/intro-popover';
import { UserSettingCode } from '../../../../shared/types/user-setting';
import {
  hideSideBarTabs,
  leadFinderViewOnly,
} from '../../../../shared/utils/user-details';
import BetterBugButton from './components/better-bug-button';

const Sidebar: React.FC<IProps> = ({
  agencyConfig,
  agencyConfigRequestStatus,

  isMultichannelActive,
  isAgency,
  client,
  hasVisitedAgencyPortal,
  handleHasVisitedAgencyPortal,

  hasDomains,
  hasDedicatedIps,

  planCode,
  unreadEmailThreadsCount,
  sendGetUnreadEmailThreadsCountRequest,
  getUserSettingsRequestStatus,
  isDemoAccount,
  updateShowSidebarValue,
  sendShowSidebarRequest,
  meta,
  leadFinder,
}) => {
  const prevBaseRouteValue = useRef<string>();
  const route = history.location.pathname;
  const { baseRoute, isBase } = useBaseRoute(route);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLElement | null>(null);

  const [shouldShowSidebarInfo, setShouldShowSidebarInfo] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showIntroPopover, setShowIntroPopover] = useState(false);

  const [navItems, setNavItems] = useState<NavItemType[]>([]);
  const [moreNavItems, setMoreNavItems] = useState<NavItemType[]>([]);

  const [preservedRouteForCRM, setPreservedRouteForCRM] = useState<
    string | null
  >(null);
  const isLeadFinderViewOnly = leadFinderViewOnly();

  const shouldShowChatSupport = useMemo(
    () =>
      hasPermission(Permissions.CHAT_SUPPORT_SHOW) &&
      !document.referrer.includes('apus.saleshandy'),
    [],
  );

  const handleSidebarClick = (event?: React.MouseEvent) => {
    if (showIntroPopover) {
      setShowIntroPopover(false);
    }

    if (isTouchDevice) {
      // Prevent toggling sidebar if the click originated from a nav item chevron
      // (to expand/collapse sub nav items)
      if (event && (event.target as HTMLElement).closest('.nav-item-chevron')) {
        return;
      }
      setIsExpanded(!isExpanded);
    }
  };

  const handleShouldShowSidebarInfo = () => {
    setShouldShowSidebarInfo(false);
    setIsSidebarInfoShown('false');
  };

  const {
    filteredNavItems,
    filteredFixedNavItems,
    filteredBottomNavItems,
  } = useMemo(
    () =>
      filterNavItems({
        isAgency,
        client,
        isMultichannelActive,
        hasDomains,
        hasDedicatedIps,
        leadFinderVersion: leadFinder?.version,
      }),
    [
      isAgency,
      client,
      isMultichannelActive,
      hasDomains,
      hasDedicatedIps,
      leadFinder?.version,
    ],
  );

  const isSidebarHidden = useMemo(
    () =>
      route.includes(Routes.Setting_Billing_Upgrade_Lead_Finder_Plan) ||
      route.includes(Routes.Setting_Billing_Upgrade_Email_Outreach_Plan) ||
      route.includes(Routes.Setting_Billing_Inbox_Placement_Radar),
    [route],
  );

  const calculateVisibleItems = () => {
    if (!scrollbarRef.current || !navRef.current) return;

    const containerHeight = scrollbarRef.current.clientHeight;
    const itemHeight = 36;
    const gap = 8;
    const moreButtonHeight = 32;

    // Subtract space taken by the "More" button
    const availableHeight = containerHeight - moreButtonHeight;

    // Each item takes itemHeight + gap, but no gap after the last visible item
    const maxVisibleItems = Math.floor(
      (availableHeight + gap) / (itemHeight + gap),
    );

    const visibleNavItems = filteredNavItems.slice(
      0,
      Math.max(1, maxVisibleItems),
    );
    const hiddenNavItems = filteredNavItems.slice(maxVisibleItems);

    setNavItems(visibleNavItems);
    setMoreNavItems([...hiddenNavItems, ...filteredFixedNavItems]);
  };

  useEffect(() => {
    calculateVisibleItems();

    const handleResize = () => {
      calculateVisibleItems();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        'ontouchstart' in window || navigator.maxTouchPoints > 0,
      );
    };

    checkTouchDevice();
  }, []);

  useEffect(() => {
    calculateVisibleItems();
  }, [
    filteredNavItems,
    filteredFixedNavItems,
    filteredBottomNavItems,
    isSidebarHidden,
  ]);

  useEffect(() => {
    if (
      planCode &&
      hasPermission(Permissions.UNIFIED_INBOX_READ) &&
      unreadEmailThreadsCount < UNREAD_MAX_LIMIT &&
      prevBaseRouteValue?.current !== baseRoute &&
      (isBase || unreadEmailThreadsCount === null)
    ) {
      sendGetUnreadEmailThreadsCountRequest({
        shouldCalculateFilterCount: false,
      });
      prevBaseRouteValue.current = baseRoute;
    }

    const hasVisitedAgencyPortalInLS = getHasVisitedAgencyPortal() === 'true';

    handleHasVisitedAgencyPortal(hasVisitedAgencyPortalInLS);
  }, [baseRoute, isBase]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getUserSettingsRequestStatus,
      onSuccess: () => {
        const isSidebarInfoShown = getIsSidebarInfoShown();
        if (isDemoAccount && !isSidebarInfoShown) {
          setShouldShowSidebarInfo(true);
          setIsSidebarInfoShown('true');
        }
      },
    });
  }, [getUserSettingsRequestStatus]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (
      getIsRequestSucceeded(getUserSettingsRequestStatus) &&
      getIsRequestSucceeded(agencyConfigRequestStatus)
    ) {
      if (!isDemoAccount && isShowSidebarIntro(meta)) {
        timeoutId = setTimeout(() => {
          setShowIntroPopover(true);
        }, 400);
        setIsExpanded(true);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [getUserSettingsRequestStatus, agencyConfigRequestStatus]);

  useEffect(() => {
    if (
      window?.location?.pathname?.startsWith('/crm/list') ||
      window?.location?.pathname?.startsWith('/crm/kanban')
    ) {
      // do not reset on other route change to preserve the view, reset on refresh
      setPreservedRouteForCRM(window?.location?.pathname);
    }
  }, [window?.location?.pathname]);

  const appSidebarClasses = useMemo(
    () =>
      classNames([
        'app-sidebar',
        {
          'app-sidebar__expanded': isExpanded,
          'app-sidebar__hidden': isSidebarHidden,
          'app-sidebar__isDemoAccount': shouldShowSidebarInfo,
          'app-sidebar__isSidebarIntro': showIntroPopover,
        },
      ]),
    [isExpanded, shouldShowSidebarInfo, showIntroPopover, route],
  );

  return (
    <div
      className={appSidebarClasses}
      ref={sidebarRef}
      onClick={handleSidebarClick}
      role="presentation"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => {
        if (showIntroPopover) return;

        setIsExpanded(false);
        if (scrollbarRef.current) {
          scrollbarRef.current.scrollTop = 0;
        }
      }}
    >
      <div className="app-sidebar__header">
        <Link
          to={hideSideBarTabs() || isLeadFinderViewOnly ? '#' : '/sequence'}
        >
          <Logo
            agencyConfig={agencyConfig}
            agencyConfigRequestStatus={agencyConfigRequestStatus}
          />
        </Link>
      </div>

      <PerfectScrollbar
        containerRef={(el) => {
          scrollbarRef.current = el;
        }}
      >
        <div className="app-sidebar__nav" ref={navRef}>
          {navItems
            ?.filter((item) => {
              const onlyReports = hideSideBarTabs();
              if (onlyReports) {
                return item?.key === 'reports';
              }

              if (isLeadFinderViewOnly) {
                return item?.key === 'leads';
              }

              return true;
            })
            .map((item) => (
              <NavItem
                isExpanded={isExpanded}
                key={item.key}
                itemKey={item.key}
                route={item.route}
                text={item.text}
                subNavItems={item?.subNavItems || []}
                hasVisitedAgencyPortal={hasVisitedAgencyPortal}
                count={unreadEmailThreadsCount}
                shouldShowSidebarInfo={shouldShowSidebarInfo}
                showInfoOnDemo={item?.showInfoOnDemo ?? true}
                handleShouldShowSidebarInfo={handleShouldShowSidebarInfo}
                currentNavItem={item}
                preservedRouteForCRM={preservedRouteForCRM}
              />
            ))}
          {!hideSideBarTabs() &&
            moreNavItems.length > 0 &&
            !isLeadFinderViewOnly && (
              <NavItem
                isExpanded={isExpanded}
                key="more"
                itemKey="more"
                route=""
                text="More"
                subNavItems={moreNavItems.flatMap((item) => [
                  item,
                  ...(item.subNavItems || []),
                ])}
                hasVisitedAgencyPortal={hasVisitedAgencyPortal}
                count={unreadEmailThreadsCount}
              />
            )}
        </div>
      </PerfectScrollbar>

      {!hideSideBarTabs() &&
        !isLeadFinderViewOnly &&
        (filteredBottomNavItems?.length > 0 || shouldShowChatSupport) && (
          <div className="app-sidebar__footer">
            <div className="app-sidebar-footer-divider" />

            {filteredBottomNavItems.map((item) => (
              <NavItem
                isExpanded={isExpanded}
                key={item.key}
                itemKey={item.key}
                route={item.route}
                text={item.text}
                subNavItems={item?.subNavItems || []}
                count={unreadEmailThreadsCount}
                hasVisitedAgencyPortal={hasVisitedAgencyPortal}
                preservedRouteForCRM={preservedRouteForCRM}
              />
            ))}
            <BetterBugButton />
            {shouldShowChatSupport && <ChatButton />}
          </div>
        )}
      <IntroPopover
        showIntroPopover={showIntroPopover}
        setShowIntroPopover={(v) => {
          setShowIntroPopover(v);
          setIsExpanded(v);
          if (!v) {
            updateShowSidebarValue();
            sendShowSidebarRequest({
              settings: [
                {
                  code: UserSettingCode.ShowSidebar,
                  value: '0',
                },
              ],
            });
          }
        }}
      />
      {shouldShowSidebarInfo && <div className="demo-account-overlay" />}
    </div>
  );
};

export default Sidebar;
